import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import {
  flip, offset as offsetMiddleware,
} from '@floating-ui/react';
import React, {
  type ReactElement, type ReactNode, type Ref, useCallback, useEffect, useId, useRef, useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Tooltip, type TooltipRefProps,
} from 'react-tooltip';
import { useTooltipContext } from '~/_shared/baseComponents/tooltip/tooltipContext';
import { CONFIG } from '~/_shared/constants/config';
import { zIndexGeneral } from '~/_shared/constants/zIndexGeneral.enum';
import { useResizeObserver } from '~/_shared/hooks/useResizeObserver';
import { type Theme } from '~/_shared/themes/theme.model';
import { useCombinedRefs } from '~/_shared/utils/hooks/useCombinedRefs';
import { mergeProps } from '~/_shared/utils/props/mergeProps';
import { isForwardRefComponent } from '~/_shared/utils/ref/ref.helpers';
import {
  getAllowedPlacements, placementMap, TooltipPlacement,
} from '../../utils/placement.util';

export { TooltipPlacement } from '../../utils/placement.util';

const arrowOffset = 8;

export enum TooltipBehavior {
  ShowOnHover = 'ShowOnHover',
  ShowAlways = 'ShowAlways',
  ShowOnOverflow = 'ShowOnOverflow',
}

const tooltipContentStyle = (theme: Theme) => css({
  '&&': {
    border: 'none',
    backgroundColor: theme.tooltipColors.backgroundColor,
    color: theme.tooltipColors.textColor,
    zIndex: zIndexGeneral.PopperTooltip,
    fontWeight: 500,
    pointerEvents: 'none',
    borderRadius: '3px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    padding: '0.4rem',
    transition: 'opacity 0.3s',
    fontSize: 'initial',
    maxWidth: 'min(600px, 50vw)',
    minWidth: 0,
  },
});

const wrapArrowStyles = (arrowStyles: CSSInterpolation) => css({
  '.arrow': [{
    zIndex: -1,
    height: 16,
    width: 16,
  }, arrowStyles],
});

export type ReactTooltipProps = Readonly<{
  tooltipContent?: ReactNode;
  placement?: TooltipPlacement;
  // ReactElement ensures we can only have one child compared to ReactNode that was used previously
  children: ReactElement;
  behavior?: TooltipBehavior;
  contentStyle?: CSSInterpolation;
  arrowStyle?: CSSInterpolation;
  offset?: number;
  overflowTolerance?: { x: number; y: number };
  delayShow?: number;
  ref?: Ref<HTMLElement>;
}>;

const getIsOpen = ({
  behavior,
  isOverflowing,
  isOpen,
  isMobileScreen,
}: Readonly<{
  behavior: TooltipBehavior;
  isOverflowing: boolean;
  isOpen: boolean;
  isMobileScreen: boolean;
}>) => {
  if (isMobileScreen) {
    return false;
  }
  if (behavior === TooltipBehavior.ShowAlways) {
    return true;
  }
  if (behavior === TooltipBehavior.ShowOnOverflow) {
    return isOverflowing ? isOpen : false;
  }
  return isOpen;
};

export const TooltipComponent = ({
  placement = TooltipPlacement.Top,
  behavior = TooltipBehavior.ShowOnHover,
  children,
  delayShow,
  contentStyle,
  arrowStyle,
  tooltipContent,
  offset = 0,
  overflowTolerance = { x: 0, y: 0 },
  ...restProps
}: ReactTooltipProps) => {
  const tooltipId = `${useId()}-${behavior}`;
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isOpen, setIsOpenInternal] = useState(false);
  const { isMobileScreen, lastFocusedElementRef } = useTooltipContext();
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<TooltipRefProps>(null);
  const trigger = React.Children.only(children);
  const ignoreNextOpenAttempt = useRef(false);
  const triggerElement = triggerRef.current;

  const setIsOpen = (value: boolean) => {
    if (ignoreNextOpenAttempt.current) {
      ignoreNextOpenAttempt.current = false;
    }
    else {
      setIsOpenInternal(value);
    }
  };

  const checkOverflowVisibility = useCallback(() => {
    if (triggerElement && (
      (
        triggerElement.scrollWidth - triggerElement.clientWidth) > overflowTolerance.x
        || (triggerElement.scrollHeight - triggerElement.clientHeight) > overflowTolerance.y
    )
    ) {
      setIsOverflowing(true);
    }
    else {
      setIsOverflowing(false);
    }
  }, [overflowTolerance.x, overflowTolerance.y, triggerElement]);

  useEffect(() => {
    if (triggerElement) {
      const ignoreNextFocusWhenDialogClosed = (event: FocusEvent) => {
        const blurredElement: Element | null = event.relatedTarget as Element | null ?? lastFocusedElementRef.current;

        if (blurredElement?.matches('*[role="dialog"], *[role="dialog"] *, *[data-element-type="modal-overlay"]')) {
          // When a dialog is closed and focus is returned to the tooltip.
          ignoreNextOpenAttempt.current = true;
        }
      };
      triggerElement.addEventListener('focus', ignoreNextFocusWhenDialogClosed);
      return () => {
        triggerElement.removeEventListener('focus', ignoreNextFocusWhenDialogClosed);
      };
    }

    return undefined;
  }, [lastFocusedElementRef, triggerElement]);

  useResizeObserver({ onResize: checkOverflowVisibility, ref: triggerRef });

  useEffect(() => {
    if ((CONFIG.MODE !== 'development') || !tooltipContent) {
      return;
    }

    const isDOMElement = typeof trigger.type === 'string';
    const isForwardRef = isForwardRefComponent(trigger);

    if (!isDOMElement && !isForwardRef) {
      console.error(`${__filename}: Tooltip needs to work with a ref. The child you supplied does not appear to be ref-able. Did you forget to forwardRef?`);
    }
  }, [trigger, tooltipContent]);

  useEffect(() => {
    if (triggerElement && behavior === TooltipBehavior.ShowOnOverflow) {
      checkOverflowVisibility();
      const observer = new MutationObserver(() => {
        checkOverflowVisibility();
      });

      observer.observe(triggerElement, {
        childList: true,
        characterData: true,
        subtree: true,
      });

      return () => observer.disconnect();
    }

    return undefined;
  }, [triggerElement, behavior, checkOverflowVisibility]);

  const triggerProps = {
    'data-tooltip-id': tooltipId,
    onClick: useCallback(() => {
      if (behavior !== TooltipBehavior.ShowAlways) {
        tooltipRef.current?.close();
        setIsOpen(false);
      }
    }, [behavior]),
    ref: useCombinedRefs(triggerRef, restProps.ref),
  };

  const triggerWithExtraProps = React.cloneElement(trigger,
    mergeProps(restProps, triggerProps, trigger.props as ReactTooltipProps),
  );

  const isTooltipOpen = getIsOpen({ behavior, isOverflowing, isOpen, isMobileScreen });

  return (
    <>
      {triggerWithExtraProps}
      {ReactDOM.createPortal(
        <Tooltip
          ref={tooltipRef}
          id={tooltipId}
          setIsOpen={setIsOpen}
          css={[tooltipContentStyle, wrapArrowStyles(arrowStyle), contentStyle]}
          classNameArrow="arrow"
          positionStrategy="absolute"
          anchorSelect={`*[data-tooltip-id='${tooltipId}']`}
          delayShow={delayShow}
          place={placementMap[placement]}
          isOpen={isTooltipOpen}
          imperativeModeOnly={behavior === TooltipBehavior.ShowAlways}
          middlewares={[
            flip({
              fallbackStrategy: 'initialPlacement',
              fallbackPlacements: getAllowedPlacements(placement),
            }),
            offsetMiddleware(offset + arrowOffset),
          ]}
          hidden={!isTooltipOpen}
          opacity={1}
        >
          {tooltipContent}
        </Tooltip>,
        document.getElementById('tooltip-portal') as HTMLElement,
      )}
    </>
  );
};
